import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, AlertCircle, CheckCircle, Info } from 'lucide-react';
import { AppShell } from '../components/Layout';
import { usePatient } from '../contexts/PatientContext';
import { API_URL, API_KEY } from '../config';

// SHA-256 hash using Web Crypto API
async function sha256(message) {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Build the raw ID string: INITIALS-DOB-MOTHER_INITIALS
// e.g. Jack Smith | 1980-02-20 | Jolly King  →  JS-1980-02-20-JK
function buildRawId(fullName, dob, motherName) {
  const initials = fullName.trim().split(/\s+/).map(w => w.charAt(0).toUpperCase()).join('');
  const motherInitials = motherName.trim().split(/\s+/).map(w => w.charAt(0).toUpperCase()).join('');
  return `${initials}-${dob}-${motherInitials}`;
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAYS   = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));
const YEARS  = Array.from({ length: 120 }, (_, i) => String(new Date().getFullYear() - i));

export default function PatientScreen() {
  const navigate = useNavigate();
  const { setPatient } = usePatient();
  const [form, setForm] = useState({ fullName: '', motherName: '' });
  const [dob, setDob] = useState({ month: '', day: '', year: '' });
  const [rawId, setRawId] = useState('');
  const [patientId, setPatientId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const set = (field) => (e) => {
    let val = e.target.value;
    if (field === 'fullName' || field === 'motherName') {
      val = val.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
    }
    setForm(f => ({ ...f, [field]: val }));
  };

  // Combine dob parts → YYYY-MM-DD
  const dobString = dob.year && dob.month && dob.day
    ? `${dob.year}-${dob.month}-${dob.day}`
    : '';

  // Recompute raw label + hashed ID whenever form changes
  useEffect(() => {
    const { fullName, motherName } = form;
    if (!fullName.trim() || !dobString || !motherName.trim()) {
      setRawId('');
      setPatientId('');
      return;
    }
    const raw = buildRawId(fullName, dobString, motherName);
    setRawId(raw);
    sha256(raw).then(hash => setPatientId(`PAT-${hash.slice(0, 12).toUpperCase()}`));
  }, [form, dobString]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const { fullName, motherName } = form;

    if (!fullName.trim() || !dobString || !motherName.trim()) {
      setError("Full Name, Date of Birth, and Mother's Name are required.");
      return;
    }
    if (!patientId) {
      setError('Patient ID is not ready yet. Please wait a moment.');
      return;
    }

    setPatient({
      full_name: fullName.trim(),
      dob: dobString,
      mother_name: motherName.trim(),
      patient_id: patientId,
      raw_id: rawId,
    });

    setLoading(true);
    const payload = { full_name: fullName.trim(), dob: dobString, patient_id: patientId };

    try {
      const res = await fetch(`${API_URL}/add-patient`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Cytometrix-API-KEY': API_KEY },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(true);
        setTimeout(() => navigate('/new-analysis'), 1500);
      } else {
        setError(data.message || 'Failed to save patient.');
      }
    } catch {
      setError('Cannot connect to server.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell title="Patient Registration">
      <div className="page-header">
        <h1 className="page-title">New Patient</h1>
        <p className="page-subtitle">Register a patient before starting wound analysis</p>
      </div>

      {/* Outer grid: 2 cols desktop → 1 col mobile */}
      <div
        className="patient-outer-grid"
        style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 360px', gap: 24, alignItems: 'start' }}
      >
        <style>{`
          @media (max-width: 720px) {
            .patient-outer-grid { grid-template-columns: minmax(0,1fr) !important; }
          }
        `}</style>

        {/* ── Form card ── */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">
              <div className="card-icon"><UserPlus size={16} /></div>
              Patient Information
            </div>
          </div>

          {error   && <div className="alert alert-error"><AlertCircle size={16} />{error}</div>}
          {success && <div className="alert alert-success"><CheckCircle size={16} />Patient saved! Redirecting to analysis...</div>}

          <form onSubmit={handleSubmit}>

            {/* Full Name */}
            <div className="form-group">
              <label className="form-label">Full Name</label>
              <input
                className="form-control"
                type="text"
                placeholder="Patient Full Name"
                value={form.fullName}
                onChange={set('fullName')}
              />
            </div>

            {/* Date of Birth — three selects, no native date picker */}
            <div className="form-group">
              <label className="form-label">Date of Birth</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <select
                  className="form-control"
                  value={dob.month}
                  onChange={e => setDob(d => ({ ...d, month: e.target.value }))}
                  style={{ flex: '2 1 0', minWidth: 0 }}
                >
                  <option value="">Month</option>
                  {MONTHS.map((m, i) => (
                    <option key={m} value={String(i + 1).padStart(2, '0')}>{m}</option>
                  ))}
                </select>

                <select
                  className="form-control"
                  value={dob.day}
                  onChange={e => setDob(d => ({ ...d, day: e.target.value }))}
                  style={{ flex: '1 1 0', minWidth: 0 }}
                >
                  <option value="">Day</option>
                  {DAYS.map(d => (
                    <option key={d} value={d}>{parseInt(d)}</option>
                  ))}
                </select>

                <select
                  className="form-control"
                  value={dob.year}
                  onChange={e => setDob(d => ({ ...d, year: e.target.value }))}
                  style={{ flex: '2 1 0', minWidth: 0 }}
                >
                  <option value="">Year</option>
                  {YEARS.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Mother's Name */}
            <div className="form-group">
              <label className="form-label">Mother's First &amp; Last Name</label>
              <input
                className="form-control"
                type="text"
                placeholder="e.g. Jolly King"
                value={form.motherName}
                onChange={set('motherName')}
              />
            </div>

            {/* Patient ID */}
            <div className="form-group">
              <label className="form-label">
                Patient ID{' '}
                <span style={{ color: 'var(--text-muted)', fontWeight: 400, textTransform: 'none', fontSize: '0.75rem' }}>
                  (auto-generated)
                </span>
              </label>
              <input
                className="form-control"
                type="text"
                value={patientId}
                readOnly
                disabled
                style={{ fontFamily: 'var(--font-mono)', fontSize: '0.9rem', letterSpacing: '0.05em' }}
              />
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
              <button type="button" className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary flex-1" disabled={loading || success}>
                {loading ? <><span className="spinner" /> Saving...</> : 'Save & Continue'}
              </button>
            </div>
          </form>
        </div>

        {/* ── Info panel ── */}
        <div className="card">
          <div className="card-header" style={{ marginBottom: 16, paddingBottom: 12 }}>
            <div className="card-title">
              <div className="card-icon" style={{ background: 'rgba(13,148,136,0.1)', color: 'var(--teal)' }}>
                <Info size={16} />
              </div>
              Patient ID Format
            </div>
          </div>

          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
            A human-readable key is built first, then SHA-256 hashed to produce a 12-character ID.
          </p>

          <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-md)', padding: 16, fontFamily: 'var(--font-mono)', fontSize: '0.82rem', marginBottom: 12, wordBreak: 'break-word' }}>
            PAT-<span style={{ color: 'var(--blue-primary)' }}>SHA256(</span>
            <span style={{ color: 'var(--teal)' }}>INITIALS</span>-
            <span style={{ color: 'var(--navy)' }}>DOB</span>-
            <span style={{ color: 'var(--teal)' }}>MOTHER_INITIALS</span>
            <span style={{ color: 'var(--blue-primary)' }}>).slice(0,12)</span>
          </div>

          <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', lineHeight: 1.7 }}>
            <strong>Example:</strong><br />
            Jack Smith · 1980-02-20 · Jolly King<br />
            Raw key:{' '}
            <code style={{ fontFamily: 'var(--font-mono)', background: 'var(--surface)', padding: '2px 6px', borderRadius: 4 }}>
              JS-1980-02-20-JK
            </code>
            <br />
            hashed to:{' '}
            <code style={{ fontFamily: 'var(--font-mono)', background: 'var(--surface)', padding: '2px 6px', borderRadius: 4 }}>
              PAT-XXXXXXXXXXXX
            </code>
          </div>

          {rawId && (
            <div style={{ marginTop: 20, padding: 14, background: 'rgba(27,108,168,0.06)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(27,108,168,0.15)', wordBreak: 'break-all' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--blue-primary)', marginBottom: 4 }}>
                Raw Key
              </div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1rem', fontWeight: 500, color: 'var(--navy)', marginBottom: 12 }}>
                {rawId}
              </div>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--teal)', marginBottom: 4 }}>
                Patient ID
              </div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1rem', fontWeight: 600, color: 'var(--navy)' }}>
                {patientId}
              </div>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
